import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import TableButtonOpen from '../../../components/table/TableButtonOpen'
import { RouteConstants } from '../../../navigation/navigation-types'
import TableButtonShow from '../../../components/table/TableButtonShow'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { Link } from 'react-router-dom'
import { UnpaidInvoiceDto } from '../../../services/api/sharedTypes/Dashboard'
import { getNumberRoundedInFormattedString } from '../../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Název Události',
    },
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Jméno klienta',
    },
    {
        id: 'daysAfterDueDate',
        numeric: true,
        disablePadding: false,
        label: 'Dny po splatnosti',
    },
    {
        id: 'invoiced',
        numeric: true,
        disablePadding: false,
        label: 'Čáskta (Kč)',
        align: 'right',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface UnpaidInvoicesTableProps {
    unpaidInvoices: UnpaidInvoiceDto[]
}

const UnpaidInvoicesTable: React.FC<UnpaidInvoicesTableProps> = ({ unpaidInvoices }: UnpaidInvoicesTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('daysAfterDueDate')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                Faktury po datu splatnosti. {unpaidInvoices.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={unpaidInvoices.length}
                        />
                        <TableBody>
                            {stableSort(
                                unpaidInvoices.map((row) => {
                                    return {
                                        ...row,
                                        eventIdName: row.eventIdName.name,
                                        eventName: row.eventIdName.name,
                                        eventId: row.eventIdName.id ?? '',
                                        clientIdName: row.clientIdName.name,
                                        clientId: row.clientIdName.id ?? '',
                                        clientName: row.clientIdName.name,
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow hover tabIndex={-1} key={index} style={{ cursor: 'pointer' }}>
                                            <CustomTableCell isText style={{ minWidth: 140 }} route={RouteConstants.clients + '/' + row.clientId}>
                                                {row.eventName}
                                            </CustomTableCell>
                                            <CustomTableCell isText route={RouteConstants.clients + '/' + row.clientId}>
                                                {row.clientName}
                                            </CustomTableCell>
                                            <CustomTableCell isText style={{ minWidth: 160 }} route={RouteConstants.events + '/' + row.eventId}>
                                                {row.daysAfterDueDate}
                                            </CustomTableCell>
                                            <CustomTableCell align='right' style={{ minWidth: 140 }} route={RouteConstants.events + '/' + row.eventId}>
                                                {getNumberRoundedInFormattedString(row.invoiced)}
                                            </CustomTableCell>
                                            <CustomTableCell align='right'>
                                                <Stack spacing={2} direction='row' justifyContent='end'>
                                                    <Link to={RouteConstants.invoices + '/' + row.invoiceId}>
                                                        <TableButtonShow />
                                                    </Link>
                                                    <TableButtonOpen route={RouteConstants.events} id={row.eventId} />
                                                </Stack>
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={unpaidInvoices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default UnpaidInvoicesTable
